import type { CmsPagesExportFile, CmsPagesImportPreview } from "../../../../types/cms";
import { isSectionHome, sectionPath } from "../../pageAddress";
import { importImagesSummary } from "../../pagesTransfer/importImages";
import {
  type ImportSidebarAction,
  type ImportSidebarSelection,
  describeSidebarChange,
  importSummary,
  previewPageSection,
  sidebarMatches,
} from "../../pagesTransfer/pagesImport";
import styles from "./PagesImportModal.module.css";

type ImportPreviewProps = {
  preview: CmsPagesImportPreview;
  exportFile: CmsPagesExportFile;
  overwriteKeys: string[];
  onOverwriteChange: (key: string, overwrite: boolean) => void;
  sidebarSelection: ImportSidebarSelection;
  onSidebarChange: (selection: ImportSidebarSelection) => void;
};

const STATUS_LABELS = {
  create: "New",
  exists: "Already exists",
  blocked: "Blocked",
};

const OVERWRITE_WARNING =
  "Replaces this page's content, layout and sidebar with the file's version. It will be set to draft.";

export function ImportPreview({
  preview,
  exportFile,
  overwriteKeys,
  onOverwriteChange,
  sidebarSelection,
  onSidebarChange,
}: ImportPreviewProps) {
  const matches = preview.sidebar ? sidebarMatches(preview.sidebar) : [];
  const imagesSummary = importImagesSummary(preview, exportFile);

  function chooseSidebarAction(action: ImportSidebarAction) {
    onSidebarChange({ action, menuId: action === "join" ? sidebarSelection.menuId : "" });
  }

  return (
    <div className={styles.preview}>
      <table className={styles.pagesTable}>
        <caption className={styles.tableCaption}>Pages in this file</caption>
        <thead>
          <tr>
            <th scope="col">Title</th>
            <th scope="col">Address</th>
            <th scope="col">Status</th>
            <th scope="col">What happens</th>
          </tr>
        </thead>
        <tbody>
          {preview.pages.map((page, index) => {
            const overwrite = overwriteKeys.includes(page.key);
            const warningId = `import-overwrite-warning-${index}`;
            const sidebarChange = describeSidebarChange(page, preview.sidebar, sidebarSelection);
            const address = sectionPath(previewPageSection(page, exportFile), page.slug);
            return (
              <tr key={page.key} className={overwrite ? styles.overwriteRow : undefined}>
                <td>{page.title}</td>
                <td className={styles.slug}>
                  {isSectionHome(page) ? `Section home (${address})` : address}
                </td>
                <td>
                  {STATUS_LABELS[page.action]}
                  {page.status === "published" && (
                    <span className={styles.liveNote}>Published on this site</span>
                  )}
                </td>
                <td>
                  {page.action === "create" ? (
                    "Will be created"
                  ) : page.action === "blocked" ? (
                    <span className={styles.blockedReason}>{page.reason}</span>
                  ) : (
                    <div
                      className={styles.overwriteChoice}
                      role="radiogroup"
                      aria-label={`What to do with ${page.title}`}
                    >
                      <label className={styles.inlineOption}>
                        <input
                          type="radio"
                          name={`import-page-${index}`}
                          checked={!overwrite}
                          onChange={() => onOverwriteChange(page.key, false)}
                        />
                        Keep existing
                      </label>
                      <label className={styles.inlineOption}>
                        <input
                          type="radio"
                          name={`import-page-${index}`}
                          checked={overwrite}
                          onChange={() => onOverwriteChange(page.key, true)}
                          aria-describedby={warningId}
                        />
                        Overwrite
                      </label>
                      <span id={warningId} className={styles.overwriteWarning}>
                        {OVERWRITE_WARNING}
                      </span>
                      {overwrite && sidebarChange && (
                        <span className={styles.sidebarChange}>{sidebarChange}</span>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {imagesSummary && <p className={styles.hint}>{imagesSummary}</p>}

      {preview.sidebar && (
        <fieldset className={styles.sidebarChoice}>
          <legend className={styles.legend}>Sidebar</legend>

          <p className={styles.hint}>The file includes the sidebar “{preview.sidebar.title}”.</p>

          <div className={styles.sidebarOption}>
            <label className={styles.inlineOption}>
              <input
                type="radio"
                name="import-sidebar"
                checked={sidebarSelection.action === "join"}
                onChange={() => chooseSidebarAction("join")}
                disabled={matches.length === 0}
                aria-describedby={matches.length === 0 ? "import-sidebar-no-matches" : undefined}
              />
              Use the same sidebar as…
            </label>

            {matches.length === 0 ? (
              <span id="import-sidebar-no-matches" className={styles.hint}>
                No sidebars here to match it with
              </span>
            ) : (
              <select
                className={styles.select}
                aria-label="Sidebar to use"
                value={sidebarSelection.menuId}
                onChange={event => onSidebarChange({ action: "join", menuId: event.target.value })}
              >
                <option value="">Choose a sidebar</option>
                {matches.map(match => (
                  <option key={match.menuId} value={match.menuId}>
                    {match.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          <label className={styles.inlineOption}>
            <input
              type="radio"
              name="import-sidebar"
              checked={sidebarSelection.action === "new"}
              onChange={() => chooseSidebarAction("new")}
            />
            Start a new sidebar
          </label>

          <label className={styles.inlineOption}>
            <input
              type="radio"
              name="import-sidebar"
              checked={sidebarSelection.action === "none"}
              onChange={() => chooseSidebarAction("none")}
            />
            No sidebar (simple pages)
          </label>
        </fieldset>
      )}

      <p className={styles.summary}>{importSummary(preview, overwriteKeys)}</p>
    </div>
  );
}
